/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { Link } from '@fluentui/react/lib/Link';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppStore } from 'src/core/stores/app/AppStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';
import { openUrlInNewTab } from 'src/core/utils/routingUtils';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { getProjectCreationItems, ProjectsCommandBar } from 'src/languageStudio/appShell/landingPage/components/ProjectsCommandBar';
import { ProjectsTable } from 'src/languageStudio/appShell/landingPage/components/ProjectsTable';
import { getImageSrc } from 'src/languageStudio/appShell/landingPage/utils/landingPageUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

const messages = defineMessages({
    createNew: { id: 'languageStudio.customProjectsSection.createNew', defaultMessage: 'Create new' },
    selectResource: { id: 'languageStudio.customProjectsSection.selectResource', defaultMessage: 'Select a resource' },
    title: { id: 'languageStudio.customProjectsSection.title', defaultMessage: 'Recent custom projects you’ve worked on' },
    viewAllProjects: { id: 'languageStudio.customProjectsSection.viewAllProjects', defaultMessage: 'View all projects' },
    emptyStateMessage: {
        id: 'languageStudio.customProjectsSection.emptyStateMessage',
        defaultMessage: `You don’t have any recent projects yet. Start with one of the custom capabilities
        to create a new project. The list of recent projects you’ve worked on will then appear here.`
    },
    noResourceSelectedMessage: {
        id: 'languageStudio.customProjectsSection.noResourceSelectedMessage',
        defaultMessage: `You need to select an Azure resource in order to see your recent
        custom projects or try out prebuilt experiences.`
    },
    emptyStateImageAltText: {
        id: 'languageStudio.customProjectsSection.emptyStateImageAltText',
        defaultMessage: 'Empty state image.'
    }
});

const sectionTheme = getScopedTheme('CustomProjectsSection');
const placeholderContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { backgroundColor: sectionTheme.emptyStateColor, borderRadius: 8 }
});
const sectionStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `1px solid ${sectionTheme.sectionBorderColor}` }
});

/**
 * Displays the section of creating a customized projects.
 */
export const CustomProjectsSection = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const appsLocalStoreHouse = useLocalStore<ResourceStoreHouse<AppStore>>(createResourceStoreHouse);

    const [sortKey, setSortKey] = React.useState<SortKeyType<AppStore>>({ key: 'modifiedDate', order: 'desc' });

    const azureResource = rootStore.azureStore.session.csAccountStore;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => {
        if (rootStore.serviceKey.value) {
            dispatcher.dispatch('loadLanguageStudioProjects', { localStore: appsLocalStoreHouse });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id]);

    const isResourceSelected = Boolean(azureResource?.id);

    const projects = appsLocalStoreHouse.items
        .slice(0, 5)
        .filter(
            v =>
                (v.appType as string) !== 'CustomConversationSummarization' &&
                (v.appType as string) !== 'CustomAbstractiveSummarization' &&
                (v.appType as string) !== 'CustomTextSentiment' &&
                (v.appType as string) !== 'CustomHealthcare'
        );
    const orderedProjects = orderBy(projects, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]);
    const displayPlaceholderView = !isResourceSelected || (appsLocalStoreHouse.tracker.isCompleted && !projects.length);

    const noResourceMessage = (
        <LuisText>
            {localize(messages.noResourceSelectedMessage)}
            &nbsp;
            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                {localize(messages.selectResource)}
            </Link>
        </LuisText>
    );

    const noProjectsView = (
        <Stack
            horizontal
            verticalAlign="center"
            data-automation-id="emptyStateView"
            styles={placeholderContainerStyles}
            tokens={{ childrenGap: 10, padding: 20 }}>
            <Image
                shouldStartVisible
                alt={localize(messages.emptyStateImageAltText)}
                src={getAssetsUrl(getImageSrc('customProjectsEmptyState'))}
            />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText color="black">{isResourceSelected ? localize(messages.emptyStateMessage) : noResourceMessage}</LuisText>
                {isResourceSelected && (
                    <Stack.Item>
                        <RBACTooltip
                            hidden={canWrite}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="createNewButtonLanguageToolTip"
                            componentSurroundedByToolTip={
                                <DefaultButton
                                    disabled={!canWrite}
                                    text={localize(messages.createNew)}
                                    ariaLabel={localize(messages.createNew)}
                                    menuProps={{ items: getProjectCreationItems() }}
                                />
                            }
                        />
                    </Stack.Item>
                )}
            </Stack>
        </Stack>
    );

    const handleAppClick = (app: AppStore) => {
        switch (app.appType) {
            case 'qnaMaker':
                openUrlInNewTab(routeTable.qnaMaker.projectPage(app.id));
                break;
            case 'CustomEntityRecognition':
            case 'CustomMultiLabelClassification':
            case 'CustomSingleLabelClassification':
                openUrlInNewTab(routeTable.customText.projectPage(app.id, app.appType as CustomTextProjectKind));
                break;
            case 'clu':
                openUrlInNewTab(routeTable.clu.projectPage(app.id));
                break;
            case 'Orchestration':
                openUrlInNewTab(routeTable.orchestration.projectPage(app.id));
                break;
            default:
        }
    };

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={sectionStyles}>
            <LuisText variant="large" fontWeight={500}>
                {localize(messages.title)}
            </LuisText>

            {displayPlaceholderView && noProjectsView}
            {appsLocalStoreHouse.tracker.isRunning && (
                <Shimmer styles={{ root: { borderRadius: 8 } }} shimmerElements={[{ type: ShimmerElementType.line, height: 120 }]} />
            )}

            {!displayPlaceholderView && !appsLocalStoreHouse.tracker.isRunning && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <ProjectsCommandBar showFarItems={false} showDelete={false} displayedItemsCount={orderedProjects.length} />
                    <ProjectsTable
                        sortKey={sortKey}
                        items={orderedProjects}
                        onAppClick={handleAppClick}
                        isLoading={appsLocalStoreHouse.tracker.isRunning}
                        onSortColumn={c => setSortKey({ key: c.key as keyof AppStore, order: c.isSortedDescending ? 'asc' : 'desc' })}
                    />
                    <ActionButton
                        iconProps={{ iconName: 'BorderAll' }}
                        styles={{ root: { marginLeft: '42px' } }}
                        text={localize(messages.viewAllProjects)}
                        onClick={() => history.push(routeTable.languageStudio.projects())}
                    />
                </Stack>
            )}
        </Stack>
    );
});
